
import { useMemo } from 'react'

import { PlanningShiftGridRow } from '@/entities/gridTables/model/planning/PlanningShiftGridRow'
import { planningShiftTablesSelector } from '@/entities/gridTables/selectors/planningShiftTablesSelector'
import { setPlanningShiftTableLoading } from '@/entities/gridTables/slices/planningShiftTablesSlice'
import { addBreak } from '@/entities/gridTables/thunks/addBreak'
import { addLoad } from '@/entities/gridTables/thunks/addLoad'
import { lockOrder } from '@/entities/gridTables/thunks/lockOrder'
import { processUnassignOrder } from '@/entities/gridTables/thunks/processUnassignOrders'
import { removeBreak } from '@/entities/gridTables/thunks/removeBreak'
import { updatePlanningShiftTableState } from '@/entities/gridTables/thunks/updatePlanningShiftTableState'
import { fetchScenarios, planningShiftsSelectors, StopTypeEnum } from '@/entities/schedules'
import { PlanningShift } from '@/entities/schedules/models/planning/PlanningShift'
import { PlanningStop } from '@/entities/schedules/models/planning/PlanningStop'
import { selectPlanningStopsByIds } from '@/entities/schedules/selectors/planningStopsSelectors'
import { optimiseShift } from '@/entities/schedules/thunks/optimiseShift'
import { EditBreakDuration } from '@/fragments/PlanningShiftTable/EditBreakDurationDialog'
import { GridRowActions } from '@/shared/components/GridTable/models/GridRowActions'
import { useDialogContext } from '@/shared/components/Modal/DialogContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faArrowDown19, faLock, faPenToSquare, faTrashUndo } from '@fortawesome/pro-regular-svg-icons'
import { faCoffee, faTruck } from '@fortawesome/pro-solid-svg-icons'
import { useTheme } from '@mui/material'
import { EntityId } from '@reduxjs/toolkit'

export type PlanningShiftTableData = {
  planningShift: PlanningShift
  rows?: PlanningShiftGridRow[]
  stops: PlanningStop[]
  shiftId: EntityId
  isLoading: boolean
  isReadOnly: boolean
  isSolverRunning: boolean
}

export const usePlanningShiftTableData = (shiftId: EntityId): PlanningShiftTableData => {
  const planningShift = useAppSelector(state => planningShiftsSelectors.selectById(state, shiftId))
  const scenarioId = planningShift?.scenarioId
  const stops = useAppSelector(state => selectPlanningStopsByIds(state, planningShift?.stopIds))
  const planningShiftTable = useAppSelector(state => planningShiftTablesSelector.selectById(state, shiftId))
  const { openDialog } = useDialogContext()
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const setShiftLoadingState = (loadingState: boolean) => {
    // Set loading state
    dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: loadingState }))
  }

  const onResequence = async (stopId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(optimiseShift({ shiftId, fromStopId: stopId }))
    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await dispatch(updatePlanningShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onAddBreak = async (stopId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(addBreak({ shiftId, stopId: stopId }))
    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await dispatch(updatePlanningShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onRemoveBreak = async (stopId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(removeBreak({ shiftId, stopId: stopId }))
    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await dispatch(updatePlanningShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onEditBreakDuration = async (stopId: EntityId, breakDuration: string) => {
    setShiftLoadingState(true)

    // Open dialog to edit break duration
    openDialog(EditBreakDuration, {
      breakDuration: breakDuration,
      stopId,
      shiftId,
      scenarioId,
      onClose: () => {
        setShiftLoadingState(false)
      },
    })
  }

  const onAddLoad = async (stopId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(addLoad({ stopId: stopId }))
    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await dispatch(updatePlanningShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onUnassignOrder = async (orderId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(processUnassignOrder({ shiftId, orderIds: [orderId] }))
    await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
    await dispatch(updatePlanningShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const onLockOrder = async (orderId: EntityId) => {
    setShiftLoadingState(true)
    await dispatch(lockOrder({ shiftId, orderIds: [orderId] }))
    await dispatch(updatePlanningShiftTableState({ shiftId: shiftId }))
    setShiftLoadingState(false)
  }

  const rows = useMemo(() => (planningShiftTable?.rows || []).map(row => {
    let rowActions: GridRowActions = []

    if (row.stopType === StopTypeEnum.Order) {
      rowActions = [
        { type: 'action', label: 'Resequence From Here', icon: faArrowDown19, onClick: () => onResequence(row.id) },
        { type: 'action', label: 'Add Break', icon: faCoffee, onClick: () => onAddBreak(row.id) },
        { type: 'action', label: 'Add Load', icon: faTruck, onClick: () => onAddLoad(row.id) },
        { type: 'divider' },
        { type: 'action', label: 'Unassign Order', icon: faTrashUndo, onClick: () => onUnassignOrder(row.orderId) },
        { type: 'action', label: 'Lock Order', icon: faLock, onClick: () => onLockOrder(row.orderId) },
      ]
    } else if (row.stopType === StopTypeEnum.Break) {
      rowActions = [
        { type: 'action', label: 'Resequence from here', icon: faArrowDown19, onClick: () => onResequence(row.id) },
        { type: 'action', label: 'Remove Break', icon: faCoffee, uiColor: theme.palette.error.main, onClick: () => onRemoveBreak(row.id) },
        { type: 'action', label: 'Edit Break Duration', icon: faPenToSquare, onClick: () => onEditBreakDuration(row.id, row.timeAtStop) },
      ]
    }

    return {
      ...row,
      rowActions,
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [planningShiftTable?.rows])

  return {
    planningShift,
    isLoading: planningShiftTable?.isLoading || false,
    rows,
    stops,
    shiftId,
    isReadOnly: false,
    isSolverRunning: false,
  }
}